import * as React from 'react'
import Head from 'next/head'

import {
  useAddToBasket,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { InteractionLocation } from '@thg-commerce/enterprise-core/src/Basket'
import { findProductOptions } from '@thg-commerce/enterprise-product-options/src/utils/options'
import { spacing, styled, Text } from '@thg-commerce/gravity-theme'

import { ProductOptionsMap } from '../../transformers/productOptionsMap'

const ShoeSizeMeText = styled.div`
  margin: ${spacing(1)} 0;
  ${Text('bodyText', 'default')}

  // The default styling of these classes comes directly from ShoeSizeMe.
  // The styling here and !important tags are needed to override that styling.

  .size-advisor-container {
    border: none !important;
    display: flex !important;
    justify-content: flex-start;
    background: transparent !important;
  }

  .col {
    background: #f0f0f0;
    border-radius: 30px;
    height: fit-content;
    width: fit-content;
    text-align: left;

    &:hover {
      border: 1px solid black;
    }
  }

  .text {
    overflow: hidden;
    white-space: nowrap;
    padding-right: 16px;
  }

  .shoeSizeDisplay {
    display: block;
  }

  .second_text {
    display: none;
  }

  .ssm_coll {
    position: relative;
  }

  .ssm_coll_btn {
  }

  .with-size {
  }
`

export interface ProductShoeSizeMe {
  productShoeSizeMe: {
    variants: {
      inStock: boolean
      sizes: string[]
    }[]
  }
}

type ShoeSizeMeProps = {
  optionsMap: React.MutableRefObject<ProductOptionsMap>
  product: {
    sku: string
    sizes: string[]
    inStockSizes: string[]
    selectedOptions: { [optionKey: string]: string } | null
  }
}

const sortAndFilterSizeArray = (sizeArray) => {
  return sizeArray.filter((item) => !isNaN(item)).sort((a, b) => a - b)
}

export const ShoeSizeMe = ({ optionsMap, product }: ShoeSizeMeProps) => {
  const { defaultLocale } = useSiteDefinition()
  const { hasShoeSizeMeId } = useSiteConfig()
  const { execute: addToBasket } = useAddToBasket({
    forceAddToBasket: true,
  })

  const locale = defaultLocale?.split('_')[0] || 'en'

  const formatSize = (recommendedSize) => {
    return recommendedSize.includes('.5')
      ? recommendedSize
      : recommendedSize.split('.')[0]
  }

  React.useEffect(() => {
    window.setCartData = (data) => {
      cartFunction(data, optionsMap.current)
    }
  }, [optionsMap])

  const cartFunction = (data, optionsMap) => {
    const productOptions = findProductOptions(
      ['Size', 'Colour'],
      {
        Size: formatSize(data.recom_size.uk),
        Colour: product.selectedOptions?.Colour || '',
      },
      optionsMap,
    )
    if (
      !productOptions ||
      !productOptions.variant.enableAddToBasket ||
      !productOptions.variant.inStock
    ) {
      return
    }
    addToBasket(
      [
        {
          sku: productOptions.variant.sku,
          quantity: 1,
        },
      ],
      {
        fromRecommendations: false,
        location: InteractionLocation.PDP,
      },
    )
  }

  if (!product) {
    return null
  }

  return (
    <React.Fragment>
      <Head>
        <script
          src="https://shoesize.me/assets/plugin/loader.js"
          dangerouslySetInnerHTML={{
            __html: `
              {
                shopID: "${hasShoeSizeMeId}",
                locale: "${locale}",
                scale: "uk",
                kids: "true",
                targetGender: "male",
                inCart: function(data) {window.setCartData(data);}
              }
            `,
          }}
        />
      </Head>
      <ShoeSizeMeText
        className="ShoeSizeMe ssm_pdp"
        data-shoeid={product.sku}
        data-sizerun={sortAndFilterSizeArray(product.sizes)}
        data-availability={sortAndFilterSizeArray(product.inStockSizes)}
        data-testid="shoe-size-me"
      ></ShoeSizeMeText>
    </React.Fragment>
  )
}
